import React, { useEffect, useState } from 'react';
import './App.css';
import { LanguageProvider } from './LangContext';
import { AuthProvider, useAuth } from './AuthContext';
import Header from './components/Header';
import Hero from './components/Hero';
import Presale from './components/Presale';
import Games from './components/Games';
import InfoBlocks from './components/InfoBlocks';
import Sections from './components/Sections';
import Footer from './components/Footer';
import LoginGate from './LoginGate';
import DominoGame from './components/DominoGame';
import TicTacToeGame from './components/TicTacToeGame';
import LudoGame from './components/LudoGame';
import SupportButton from './components/SupportButton';

import { Toaster } from './components/ui/toaster';
import { Loader2 } from 'lucide-react';
import { captureReferralFromUrl, googleAuthApi, getStoredReferral, clearStoredReferral } from './api';
import { useToast } from './hooks/use-toast';

const Gate = () => {
  const { user, loading, finalizeLogin } = useAuth();
  const { toast } = useToast();
  const [activeGame, setActiveGame] = useState(null);
  const [oauthBusy, setOauthBusy] = useState(false);

  useEffect(() => { captureReferralFromUrl(); }, []);

  // Handle Emergent Google OAuth return: extract `#session_id=...` and exchange for JWT
  useEffect(() => {
    const hash = (window.location.hash || '').replace(/^#/, '');
    if (!hash) return;
    const params = new URLSearchParams(hash);
    const sid = params.get('session_id');
    if (!sid) return;
    // Clear the fragment immediately to avoid replay
    window.history.replaceState({}, '', window.location.pathname + window.location.search);
    setOauthBusy(true);
    const referredBy = getStoredReferral() || undefined;
    googleAuthApi.callback(sid, referredBy)
      .then((res) => {
        finalizeLogin(res);
        clearStoredReferral();
        toast({
          title: res.is_new ? `Welcome ${res.user?.username}!` : `Welcome back, ${res.user?.username}!`,
          description: res.is_new ? 'Your Google account is now linked.' : undefined,
        });
      })
      .catch((err) => {
        toast({ title: 'Google sign-in failed', description: err?.response?.data?.detail || err?.message || 'Try again', variant: 'destructive' });
      })
      .finally(() => setOauthBusy(false));
  }, [finalizeLogin, toast]);

  if (loading || oauthBusy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-white">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }
  if (!user) return <LoginGate />;

  if (activeGame === 'domino') {
    return <DominoGame onClose={() => setActiveGame(null)} />;
  }
  if (activeGame === 'tictactoe') {
    return <TicTacToeGame onClose={() => setActiveGame(null)} />;
  }
  if (activeGame === 'ludo') {
    return <LudoGame onClose={() => setActiveGame(null)} />;
  }

  return (
    <div className="App relative">
      <Header />
      <Hero />
      <Presale />
      <Games onPlay={setActiveGame} />
      <InfoBlocks />
      <Sections />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Gate />
        <Toaster />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
